import {
  Typography,
  Paper,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import StorefrontIcon from '@mui/icons-material/Storefront'
import StoreIcon from '@mui/icons-material/Store'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import PaymentIcon from '@mui/icons-material/Payment'
import ExportButton from '../../components/ExportButton'
import ProgressBar from '../../components/ProgressBar'
import { useFranchise } from '../../contexts/FranchiseContext'
import { usePageTitle } from '../../hooks/usePageTitle'
import { useAsyncExport } from '../../hooks/useAsyncExport'

function RetailerSetup() {
  usePageTitle('Lightspeed', 'Retailer Setup')
  const { fpCode } = useFranchise()

  const {
    loading,
    error,
    success,
    progressMessage,
    startExport
  } = useAsyncExport({
    exportType: 'retailer_setup'
  })

  const handleExport = async () => {
    await startExport({
      fp_code: fpCode,
      format: 'xlsx'
    })
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {!fpCode && (
          <Alert severity="info">
            Please select a franchise partner from the dropdown above to continue.
          </Alert>
        )}

        {fpCode && (
          <>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Retailer Setup Export
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Exports retailer configuration data as a multi-sheet Excel file.
                All fields from the Lightspeed API are included automatically.
              </Typography>

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Included Sheets:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <StorefrontIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Retailer"
                    secondary="Store name, timezone, currency, contact details"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <StoreIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Outlets"
                    secondary="All outlet/location configurations"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PointOfSaleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Registers"
                    secondary="All register/terminal configurations"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PaymentIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Payment Types"
                    secondary="All configured payment methods"
                  />
                </ListItem>
              </List>
            </Paper>

            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success">
                {success}
              </Alert>
            )}

            {loading && (
              <Box>
                <ProgressBar
                  indeterminate
                  message={progressMessage || "Exporting retailer setup data..."}
                />
              </Box>
            )}

            <ExportButton
              onClick={handleExport}
              loading={loading}
              disabled={!fpCode}
              text="Export Retailer Setup"
            />
          </>
        )}
      </Box>
    </Box>
  )
}

export default RetailerSetup
